import {
    type AgentAutomationEventTriggerCatalogResponse,
    type AgentAutomationListResponse,
    type AgentAutomationMemoryEventListResponse,
    type AgentAutomationPreferenceMemoryRead,
    type AgentAutomationRunListResponse,
    type AgentAutomationRunRead,
    type AutomationApiError as ApiError,
    type SlackChannelSearchChannelRead,
    type SlackV2SetupResponse,
} from '@apps/web/components/automations/types';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type ApiQueryParamsType,
    type Mode,
} from '@libraries/typescript/api_client';

export function useAgentAutomations(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/automations',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { limit: 200 },
        enabled: !!organizationId,
        mode: 'live',
        select: (response) => response as AgentAutomationListResponse,
        reason: 'agent-automations-list',
    });
}

/** Active product groups. Membership is fetched independently. */
export function useAgentAutomationGroups(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/automations/groups',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        mode: 'live',
        select: (response) => response,
        reason: 'agent-automation-groups',
    });
}

// Resolves Slack channel ids to names for display. Channel search requires
// org settings access, so callers must tolerate an error result and fall
// back to showing the raw channel id.
export function useAutomationSlackChannelNames(
    organizationId: string | null,
    channelIds: Array<string>,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/slack-v2/channels',
        method: 'get',
        enabled: organizationId !== null && channelIds.length > 0,
        pathParams: {
            organization_id: organizationId ?? '',
        },
        queryParams: {
            query: '',
            // The endpoint caps selected_channel_ids at 10 entries.
            selected_channel_ids: channelIds.slice(0, 10),
            limit: 10,
        },
        mode: 'live',
        select: (response) =>
            response.channels as Array<SlackChannelSearchChannelRead>,
        reason: 'automation-slack-channel-names',
    });
}

export function useAutomationSlackSetup(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/slack-v2/setup',
        method: 'get',
        enabled: organizationId !== null,
        pathParams: {
            organization_id: organizationId ?? '',
        },
        select: (response) => response as SlackV2SetupResponse,
        reason: 'automation-slack-setup',
    });
}

export function useAutomationSlackChannels({
    enabled,
    organizationId,
    query,
    selectedChannelId,
}: {
    readonly enabled: boolean;
    readonly organizationId: string | null;
    readonly query: string;
    readonly selectedChannelId?: string | null;
}) {
    return useApiQuery({
        path: '/organizations/{organization_id}/slack-v2/channels',
        method: 'get',
        enabled: enabled && organizationId !== null,
        pathParams: {
            organization_id: organizationId ?? '',
        },
        queryParams: {
            query,
            selected_channel_ids:
                selectedChannelId !== null && selectedChannelId !== undefined
                    ? [selectedChannelId]
                    : undefined,
            limit: 20,
        },
        select: (response) =>
            response.channels as Array<SlackChannelSearchChannelRead>,
        reason: 'automation-slack-channels',
        keepPreviousData: true,
        dedupingInterval: 1000,
    });
}

export function useAgentAutomation(
    organizationId: string | null,
    automationId: string | null,
    options: { readonly mode?: Mode } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/automations/{automation_id}',
        method: 'get',
        pathParams:
            organizationId && automationId
                ? {
                      organization_id: organizationId,
                      automation_id: automationId,
                  }
                : undefined,
        enabled: !!organizationId && !!automationId,
        mode: options.mode,
        select: (response) => response,
        reason: 'agent-automation-detail',
    });
}

export function useAgentAutomationPreferenceMemory(
    organizationId: string | null,
    automationId: string | null,
    options: { readonly mode?: Mode } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/automations/{automation_id}/preference-memory',
        method: 'get',
        pathParams:
            organizationId && automationId
                ? {
                      organization_id: organizationId,
                      automation_id: automationId,
                  }
                : undefined,
        enabled: !!organizationId && !!automationId,
        mode: options.mode,
        select: (response) => response as AgentAutomationPreferenceMemoryRead,
        reason: 'agent-automation-preference-memory',
    });
}

export function useAgentAutomationRuns(
    organizationId: string | null,
    automationId: string | null,
    options: {
        readonly cursor?: string | null;
        readonly limit?: number;
        readonly mode?: Mode;
        readonly poll?: boolean;
    } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/automations/{automation_id}/runs',
        method: 'get',
        pathParams:
            organizationId && automationId
                ? {
                      organization_id: organizationId,
                      automation_id: automationId,
                  }
                : undefined,
        queryParams: {
            cursor: options.cursor ?? undefined,
            limit: options.limit,
        },
        enabled: !!organizationId && !!automationId,
        mode: options.mode,
        select: (response) => response as AgentAutomationRunListResponse,
        reason: 'agent-automation-runs',
        keepPreviousData: true,
        refreshInterval: (response) =>
            options.poll === false ||
            !shouldPollAutomationRuns(response?.runs ?? [])
                ? 0
                : 5_000,
        refreshWhenHidden: false,
    });
}

export function useAgentAutomationRun(
    organizationId: string | null,
    automationId: string | null,
    automationRunId: string | null,
    options: { readonly mode?: Mode; readonly poll?: boolean } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/automations/{automation_id}/runs/{automation_run_id}',
        method: 'get',
        pathParams:
            organizationId && automationId && automationRunId
                ? {
                      organization_id: organizationId,
                      automation_id: automationId,
                      automation_run_id: automationRunId,
                  }
                : undefined,
        enabled: !!organizationId && !!automationId && !!automationRunId,
        mode: options.mode,
        select: (response) => response,
        reason: 'agent-automation-run-detail',
        refreshInterval: (response) =>
            options.poll === false || !shouldPollAutomationRun(response)
                ? 0
                : 5_000,
        refreshWhenHidden: false,
    });
}

export function useAgentAutomationRunMemoryEvents(
    organizationId: string | null,
    automationId: string | null,
    automationRunId: string | null,
    options: { readonly mode?: Mode } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/automations/{automation_id}/runs/{automation_run_id}/memory-events',
        method: 'get',
        pathParams:
            organizationId && automationId && automationRunId
                ? {
                      organization_id: organizationId,
                      automation_id: automationId,
                      automation_run_id: automationRunId,
                  }
                : undefined,
        enabled: !!organizationId && !!automationId && !!automationRunId,
        mode: options.mode,
        select: (response) =>
            response as AgentAutomationMemoryEventListResponse,
        reason: 'agent-automation-run-memory-events',
    });
}

// Only actively executing or pending-approval runs warrant polling. Phoebe
// agent runs commonly rest in `idle` between turns or after their work
// finishes without a terminal transition, so polling on `idle` would mean
// keeping a 5s poll loop alive indefinitely for runs that will never change
// on their own. Users can refresh manually if they want to confirm a run
// that's resting in idle is truly done.
export function shouldPollAutomationRuns(
    runs: ReadonlyArray<
        Pick<AgentAutomationRunRead, 'run_status' | 'needs_tool_approval'>
    >,
): boolean {
    return runs.some(
        (run) => run.run_status === 'running' || run.needs_tool_approval,
    );
}

export function shouldPollAutomationRun(
    run:
        | Pick<AgentAutomationRunRead, 'run_status' | 'needs_tool_approval'>
        | null
        | undefined,
): boolean {
    return run !== null && run !== undefined && shouldPollAutomationRuns([run]);
}

export function isAutomationRunMissingError(
    error: Pick<ApiError, 'status'> | undefined,
): boolean {
    return error?.status === 404;
}

export function isAutomationMissingError(
    error: Pick<ApiError, 'status'> | undefined,
): boolean {
    return error?.status === 404;
}

export function useAgentAutomationEventTriggerCatalog(
    organizationId: string | null,
    options: { readonly enabled?: boolean; readonly mode?: Mode } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/automations/event-trigger-catalog',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && (options.enabled ?? true),
        mode: options.mode,
        select: (response) =>
            response as AgentAutomationEventTriggerCatalogResponse,
        reason: 'agent-automation-event-trigger-catalog',
    });
}

type AdminAgentAutomationsQueryParams = ApiQueryParamsType<
    '/admin/agent/automations',
    'get'
>;

type AdminAgentAutomationRunsQueryParams = ApiQueryParamsType<
    '/admin/agent/automations/runs',
    'get'
>;

export function useAdminAgentAutomationRuns(
    params?: AdminAgentAutomationRunsQueryParams,
    options: { readonly enabled?: boolean } = {},
) {
    return useApiQuery({
        path: '/admin/agent/automations/runs',
        method: 'get',
        queryParams: params,
        enabled: options.enabled ?? true,
        select: (response) => response,
        keepPreviousData: true,
        reason: 'admin-agent-automation-runs-list',
    });
}

type AdminAgentAutomationMemoriesQueryParams = ApiQueryParamsType<
    '/admin/agent/automations/memories',
    'get'
>;

export function useAdminAgentAutomationMemories(
    params?: AdminAgentAutomationMemoriesQueryParams,
    options: { readonly enabled?: boolean } = {},
) {
    return useApiQuery({
        path: '/admin/agent/automations/memories',
        method: 'get',
        queryParams: params,
        enabled: options.enabled ?? true,
        select: (response) => response,
        reason: 'admin-agent-automation-memories',
    });
}

export function useAdminAgentAutomationAttention(
    options: { readonly enabled?: boolean } = {},
) {
    return useApiQuery({
        path: '/admin/agent/automations/attention',
        method: 'get',
        enabled: options.enabled ?? true,
        select: (response) => response,
        reason: 'admin-agent-automation-attention',
    });
}

export function useAdminAgentAutomations(
    params?: AdminAgentAutomationsQueryParams,
    options: { readonly enabled?: boolean } = {},
) {
    return useApiQuery({
        path: '/admin/agent/automations',
        method: 'get',
        queryParams: params,
        enabled: options.enabled ?? true,
        select: (response) => response,
        keepPreviousData: true,
        reason: 'admin-agent-automations-list',
    });
}

export function useAdminAgentAutomationGroup(automationGroupId: string | null) {
    return useApiQuery({
        path: '/admin/agent/automations/groups/{automation_group_id}',
        method: 'get',
        pathParams: automationGroupId
            ? { automation_group_id: automationGroupId }
            : undefined,
        enabled: automationGroupId !== null,
        select: (response) => response,
        reason: 'admin-agent-automation-group-detail',
    });
}

export function formatErrorMessage(error: unknown, fallback: string): string {
    const apiError = error as ApiError | undefined;
    if (apiError?.message) {
        return apiError.message;
    }
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return fallback;
}
